import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Box,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

interface AddGameModalProps {
  show: boolean;
  onHide: () => void;
  onAddGame: (game: any) => void;
}

function AddGameModal({ show, onHide, onAddGame }: AddGameModalProps) {
  const rawgApiKey = process.env.REACT_APP_RAWG_API_KEY;

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedGame, setSelectedGame] = useState<any | null>(null);
  const [platform, setPlatform] = useState('PS5');
  const [edition, setEdition] = useState('Standard');
  const [editionName, setEditionName] = useState('');
  const [steelbook, setSteelbook] = useState(false);
  const [sealed, setSealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedGame(null);
      setPlatform('PS5');
      setEdition('Standard');
      setEditionName('');
      setSteelbook(false);
      setSealed(false);
      setError(null);
      setLoading(false);
    }
  }, [show]);

  const performSearch = useCallback(
    async (value: string) => {
      const trimmedValue = value.trim();

      if (trimmedValue.length < 2) {
        setSearchResults([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get('https://api.rawg.io/api/games', {
          params: {
            key: rawgApiKey,
            search: trimmedValue,
            page_size: 12,
          },
        });

        const mappedGames = (response.data.results || []).map((game: any) => ({
          id: game.id,
          title: game.name,
          year: game.released || '',
          image: game.background_image || '',
        }));

        setSearchResults(mappedGames);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch game data.');
      } finally {
        setLoading(false);
      }
    },
    [rawgApiKey]
  );

  useEffect(() => {
    if (!show || selectedGame) return;

    const timeoutId = window.setTimeout(() => {
      performSearch(searchTerm);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [show, searchTerm, selectedGame, performSearch]);

  const handleAddGame = useCallback(() => {
    if (!selectedGame) return;

    onAddGame({
      title: selectedGame.title,
      year: selectedGame.year
        ? parseInt(String(selectedGame.year).split('-')[0], 10)
        : 0,
      platform,
      edition,
      editionName: edition === 'Special' ? editionName.trim() : '',
      steelbook,
      sealed,
      image: selectedGame.image || '',
    });

    onHide();
  }, [selectedGame, onAddGame, platform, edition, editionName, steelbook, sealed, onHide]);

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle>Add Game</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Search for a game"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Start typing a game title..."
            helperText={
              selectedGame
                ? 'Game selected below.'
                : searchTerm.trim().length < 2
                  ? 'Type at least 2 characters to search automatically.'
                  : 'Searching automatically as you type.'
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {selectedGame ? (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Selected Game
            </Typography>

            <Box
              component="img"
              src={selectedGame.image}
              alt={selectedGame.title}
              sx={{
                width: 220,
                maxWidth: '100%',
                borderRadius: 1,
                mb: 2,
                objectFit: 'cover',
              }}
            />

            <Typography sx={{ mb: 2 }}>
              {selectedGame.title}
            </Typography>

            <Button
              variant="outlined"
              sx={{ mb: 3 }}
              onClick={() => {
                setSelectedGame(null);
                setSearchResults([]);
              }}
            >
              Change Game
            </Button>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platform}
                label="Platform"
                onChange={(e) => setPlatform(e.target.value)}
              >
                <MenuItem value="PS1">PS1</MenuItem>
                <MenuItem value="PS2">PS2</MenuItem>
                <MenuItem value="PS3">PS3</MenuItem>
                <MenuItem value="PS4">PS4</MenuItem>
                <MenuItem value="PS5">PS5</MenuItem>
                <MenuItem value="Switch">Switch</MenuItem>
                <MenuItem value="Switch 2">Switch 2</MenuItem>
                <MenuItem value="DS">DS</MenuItem>
                <MenuItem value="3DS">3DS</MenuItem>
                <MenuItem value="PC">PC</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Edition</InputLabel>
              <Select
                value={edition}
                label="Edition"
                onChange={(e) => {
                  setEdition(e.target.value);
                  if (e.target.value !== 'Special') {
                    setEditionName('');
                  }
                }}
              >
                <MenuItem value="Standard">Standard</MenuItem>
                <MenuItem value="Special">Special</MenuItem>
              </Select>
            </FormControl>

            {edition === 'Special' && (
              <TextField
                fullWidth
                label="Edition Name"
                placeholder="Collector's Edition"
                value={editionName}
                onChange={(e) => setEditionName(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={steelbook}
                    onChange={(e) => setSteelbook(e.target.checked)}
                  />
                }
                label="Steelbook"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={sealed}
                    onChange={(e) => setSealed(e.target.checked)}
                  />
                }
                label="Sealed"
              />
            </Box>
          </Box>
        ) : (
          <>
            {!loading && searchTerm.trim().length >= 2 && searchResults.length === 0 && !error && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                No results found.
              </Typography>
            )}

            {searchResults.length > 0 && (
              <Grid
                container
                spacing={2}
                sx={{ maxHeight: 400, overflowY: 'auto', mt: 2 }}
              >
                {searchResults.map((game) => (
                  <Grid key={game.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Box
                      component="img"
                      src={game.image}
                      alt={game.title}
                      onClick={() => setSelectedGame(game)}
                      sx={{
                        width: '100%',
                        aspectRatio: '3 / 4',
                        borderRadius: 1,
                        cursor: 'pointer',
                        objectFit: 'cover',
                        '&:hover': { opacity: 0.8 },
                      }}
                    />
                    <Typography align="center" sx={{ mt: 1 }}>
                      {game.title}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddGame}
          disabled={!selectedGame}
        >
          Add Game
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(AddGameModal);